import { AttachmentPreviewUtil, useImageSource } from '@axiom/ui';

import { ChatApi } from '../../api/chat-api';
import type {
  ChatAttachment,
  ChatMessageAttachment,
  ChatMessageEntry,
} from '../../types/chat.types';

export const useAttachmentPreviewData = ({
  attachment,
  chatId,
}: {
  attachment: ChatAttachment | ChatMessageAttachment | null;
  chatId?: ChatMessageEntry['chatId'];
}) => {
  const fileName =
    attachment && 'file' in attachment
      ? attachment.file.name
      : attachment?.fileName;

  // Gate on the name, not `file.type`: a picked file's type is often absent and a sent one has none.
  const isPreviewable =
    !!fileName && AttachmentPreviewUtil.isDisplayableImage({ fileName });

  const remoteKey =
    attachment && !('file' in attachment) && chatId && attachment.fileId
      ? `${chatId}/${attachment.fileId}`
      : null;

  const imageKey =
    attachment && 'file' in attachment ? attachment.id : remoteKey;

  return useImageSource({
    imageKey: isPreviewable ? imageKey : null,
    load: async () => {
      if (attachment && 'file' in attachment) return attachment.file;

      // Unreachable while `imageKey` is null, which the compiler cannot see from here.
      if (!chatId || !attachment?.fileId) {
        throw new Error('No attachment to read');
      }

      return ChatApi.readAttachment({ chatId, fileId: attachment.fileId });
    },
  });
};
